import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Briefcase, TrendingUp, Server, Bot, MapPin, Globe, Heart, Rocket, ArrowRight, Mail } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/Seo";
import { MarketingNav, MarketingFooter } from "@/components/MarketingChrome";

const CAREERS_EMAIL = "careers@askredline.com";

const ROLES = [
  {
    id: "sales-bdr",
    icon: TrendingUp,
    title: "Sales BDR (Business Development Representative)",
    meta: "Full-time · Remote (US)",
    body: "Open the conversation with HOA and community management companies. You'll run smart outbound, qualify the fit and book demos that turn into real pipeline.",
    points: [
      "Prospect management companies and self-managed boards by email, phone and LinkedIn",
      "Qualify needs and book demos for the founding team",
      "Keep a clean pipeline and sharpen the messaging with what you hear",
    ],
  },
  {
    id: "devops-specialist",
    icon: Server,
    title: "DevOps Specialist",
    meta: "Full-time · Remote",
    body: "Keep a multi-tenant platform fast, reliable and observable, including the background engines that page on-call managers when a community has an emergency.",
    points: [
      "Own CI/CD, infrastructure-as-code and environment reliability",
      "Build monitoring, alerting and sensible on-call for critical loops",
      "Harden multi-tenant isolation, backups and incident response",
    ],
  },
  {
    id: "ai-engineer",
    icon: Bot,
    title: "AI Engineer",
    meta: "Full-time · Remote",
    body: "Build the concierge and copilot brains, grounded retrieval over each community's documents, the realtime voice line, and the evaluations that keep answers safe and accurate.",
    points: [
      "Design retrieval/grounding pipelines over community documents and rules",
      "Ship and tune the text + realtime voice concierge experiences",
      "Build evals and guardrails so the AI stays grounded and hands off well",
    ],
  },
];

const PERKS = [
  { icon: Globe, title: "Remote-first", body: "Work from where you do your best thinking." },
  { icon: Rocket, title: "Real ownership", body: "Small team, big surface area, your work ships and matters." },
  { icon: Heart, title: "Work that helps", body: "Make everyday life in real communities easier." },
];

export default function CareersPage() {
  const jsonLd = breadcrumbLd([{ name: "Home", path: "/" }, { name: "Careers", path: "/careers" }]);
  return (
    <div className="font-body bg-sand-50 text-ink min-h-screen" data-testid="careers-page">
      <Seo
        title="Careers, Join Ask Redline"
        description="Help build the AI operating platform for HOAs and community management companies. Open roles: Sales BDR, DevOps Specialist and AI Engineer, remote."
        canonical="/careers"
        jsonLd={jsonLd}
        scrollTop
      />
      <MarketingNav />

      <section className="max-w-6xl mx-auto px-6 pt-14 pb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-redline-50 text-redline-700 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-5">
            <Briefcase className="h-3.5 w-3.5" strokeWidth={2} /> Careers
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]">Build the platform modern communities run on.</h1>
          <p className="mt-5 text-lg text-ink-700 leading-relaxed">
            We're a small, fast-moving team giving every community a 24/7 concierge and giving managers a real command center.
            If that sounds like your kind of problem, we'd love to meet you.
          </p>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-6 grid sm:grid-cols-3 gap-4">
        {PERKS.map((p) => {
          const Icon = p.icon;
          return (
            <div key={p.title} className="flex items-start gap-3 rounded-2xl border border-sand-200 bg-white p-5">
              <span className="h-9 w-9 rounded-lg bg-redline-50 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-redline-600" strokeWidth={1.75} /></span>
              <div>
                <div className="font-bold text-sm">{p.title}</div>
                <div className="text-xs text-ink-600 mt-1 leading-relaxed">{p.body}</div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="max-w-6xl mx-auto px-6 pt-8 pb-16" data-testid="careers-openings">
        <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-6">Open roles</h2>
        <div className="space-y-4">
          {ROLES.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.div key={r.id} data-testid={`careers-role-${r.id}`}
                initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-3xl border border-sand-200 bg-white p-8">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
                  <div className="flex items-start gap-4">
                    <div className="h-11 w-11 rounded-xl bg-redline-50 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-redline-600" strokeWidth={1.75} /></div>
                    <div>
                      <h3 className="font-heading text-xl font-extrabold tracking-tight">{r.title}</h3>
                      <div className="text-xs font-semibold text-ink-500 mt-1">{r.meta}</div>
                      <p className="text-sm text-ink-600 mt-3 leading-relaxed max-w-2xl">{r.body}</p>
                      <ul className="mt-4 space-y-2">
                        {r.points.map((pt) => (
                          <li key={pt} className="flex items-start gap-2.5 text-sm text-ink-700 leading-relaxed">
                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-redline-400 shrink-0" /> {pt}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <a href={`mailto:${CAREERS_EMAIL}?subject=${encodeURIComponent("Application: " + r.title)}`} data-testid={`careers-apply-${r.id}`}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white font-semibold px-6 py-3 text-sm hover:bg-ink-700 transition-colors shrink-0 self-start">
                    Apply <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="rounded-3xl border border-sand-200 bg-white p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight">Don't see your role?</h2>
            <p className="text-sm text-ink-600 mt-2 leading-relaxed max-w-xl">If you're excited about what we're building and think you'd add something, tell us how, we're always glad to meet good people.</p>
          </div>
          <a href={`mailto:${CAREERS_EMAIL}?subject=${encodeURIComponent("Introduction")}`} data-testid="careers-general-apply"
            className="inline-flex items-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-7 py-3.5 hover:bg-redline-600 transition-colors shrink-0">
            <Mail className="h-4 w-4" /> Get in touch
          </a>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
